import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Typography,
  IconButton,
  TextField,
  InputAdornment,
} from "@mui/material";

import {
  Search as SearchIcon,
  Close as CloseIcon,
  Download as DownloadIcon,
} from "@mui/icons-material";

import axios from "../../config/axios";
import { toast } from "react-toastify";
import { formatDate } from "../../helper/dateHelper";

function ReportInquiries() {
  const [reportInquiries, setReportInquiries] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState("");

  const exportToCSV = () => {
    const headers = [
      "Email",
      "Created At",
      "Requested On",
      "Number of Request",
    ];
    const csvRows = reportInquiries.map((inquiry) => [
      `"${inquiry.email}"`,
      `"${formatDate(inquiry.created_at)}"`,
      `"${formatDate(inquiry.updated_at)}"`,
      `"${inquiry.number_of_request}"`,
    ]);
    const csvContent = [headers, ...csvRows]
      .map((row) => row.join(","))
      .join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    const now = new Date().toISOString().slice(0, 19).replace(/:/g, "-");
    link.setAttribute("download", `report-inquiries-${now}.csv`);
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const fetchReportInquiries = useCallback(async () => {
    try {
      const response = await axios.get("/report-inquiries", {
        params: {
          page: page + 1,
          limit: rowsPerPage,
          search,
        },
      });

      setReportInquiries(response.data.reportInquiries || []);
      setTotal(response.data.pagination.total);
    } catch (error) {
      console.error("Error fetching report inquiries:", error);
      toast.error(
        error.response?.data?.message || "Error fetching report inquiries",
      );
    }
  }, [page, rowsPerPage, search]);

  useEffect(() => {
    fetchReportInquiries();
  }, [fetchReportInquiries]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleSearch = (event) => {
    setSearch(event.target.value);
    setPage(0);
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        p: 3,
        animation: "fadeIn 0.8s ease-in-out",
        "@keyframes fadeIn": {
          "0%": { opacity: 0, transform: "translateY(-20px)" },
          "100%": { opacity: 1, transform: "translateY(0)" },
        },
      }}
    >
      {/* Page Title */}

      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 3,
        }}
      >
        <Typography
          variant="h4"
          sx={{
            color: "#26A69A",
            fontWeight: 600,
          }}
        >
          Report Inquiries
        </Typography>
        <Button
          startIcon={<DownloadIcon />}
          onClick={exportToCSV}
          sx={{
            background: "#26A69A",
            color: "#FFFFFF",
            padding: "10px 20px",
            borderRadius: "8px",
            fontWeight: 500,
            textTransform: "none",
            transition: "all 0.3s",
            "&:hover": { background: "#009688", transform: "scale(1.02)" },
            "@media (max-width: 600px)": {
              width: "100%",
              padding: "8px 16px",
              fontSize: "14px",
            },
          }}
        >
          Export Reports
        </Button>
      </Box>

      {/* Search */}

      <TextField
        fullWidth
        variant="outlined"
        placeholder="Search report..."
        value={search}
        onChange={handleSearch}
        sx={{ mb: 3 }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon />
            </InputAdornment>
          ),
          endAdornment: search ? (
            <InputAdornment position="end">
              <IconButton onClick={() => setSearch("")}>
                <CloseIcon />
              </IconButton>
            </InputAdornment>
          ) : null,
        }}
      />

      {/* Table */}

      <TableContainer
        component={Paper}
        sx={{
          borderRadius: "16px",
          background: "rgba(255,255,255,0.95)",
        }}
      >
        <Table>
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: "bold", color: "#26A69A" }}>
                Email
              </TableCell>

              <TableCell sx={{ fontWeight: "bold", color: "#26A69A" }}>
                Created At
              </TableCell>

              <TableCell sx={{ fontWeight: "bold", color: "#26A69A" }}>
                Requested On
              </TableCell>

              <TableCell sx={{ fontWeight: "bold", color: "#26A69A" }}>
                Number of Request
              </TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {reportInquiries.map((inquiry, index) => (
              <TableRow
                key={inquiry.id}
                sx={{
                  backgroundColor:
                    index % 2 === 0 ? "rgba(255,255,255,0.1)" : "transparent",
                  "&:hover": {
                    backgroundColor: "#E0F2F1",
                  },
                }}
              >
                <TableCell>{inquiry.email}</TableCell>

                <TableCell>{formatDate(inquiry.created_at)}</TableCell>

                <TableCell>{formatDate(inquiry.updated_at)}</TableCell>

                <TableCell>{inquiry.number_of_request}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>

        {/* Pagination */}
        <TablePagination
          rowsPerPageOptions={[5, 10, 25, 50, 100, 250]}
          component="div"
          count={total}
          page={page}
          rowsPerPage={rowsPerPage}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </TableContainer>
    </Box>
  );
}

export default ReportInquiries;
